import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { usersApi } from '../../api/users.api';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

const columns = [
  { key: 'username', label: 'Username' },
  {
    key: 'email',
    label: 'Email',
    render: (row) => row.emails?.[0]?.address || '-'
  },
  {
    key: 'role',
    label: 'Type',
    render: (row) => (
      <Badge variant={row.profile?.role === 'guest' ? 'warning' : 'success'}>
        {row.profile?.role === 'guest' ? 'Guest' : 'Registered'}
      </Badge>
    )
  },
  {
    key: 'language',
    label: 'Language',
    render: (row) => (
      <Badge variant="info">
        {row.profile?.language?.toUpperCase() || 'EN'}
      </Badge>
    )
  },
  {
    key: 'verified',
    label: 'Verified',
    render: (row) => {
      if (row.profile?.role === 'guest') return '-';
      return row.emails?.[0]?.verified ? (
        <Badge variant="success">Yes</Badge>
      ) : (
        <Badge variant="warning">No</Badge>
      );
    }
  },
  {
    key: 'createdAt',
    label: 'Created',
    render: (row) => {
      if (!row.createdAt) return '-';
      return new Date(row.createdAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
];

export default function UsersListPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      const response = await usersApi.getAll({ page, search: searchTerm, limit: 20 });
      setUsers(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(1, search);
  }, [search]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await usersApi.delete(deleteId);
      setDeleteId(null);
      fetchUsers(pagination.page, search);
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <Button onClick={() => navigate('/users/new')}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Add User
        </Button>
      </div>

      <DataTable
        columns={[
          ...columns,
          {
            key: 'actions',
            label: 'Actions',
            align: 'right',
            render: (row) => (
              <div className="flex gap-2 justify-end">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/users/${row._id}/edit`);
                  }}
                  className="p-1 text-gray-500 hover:text-primary-600"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteId(row._id);
                  }}
                  className="p-1 text-gray-500 hover:text-red-600"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            )
          }
        ]}
        data={users}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => fetchUsers(page, search)}
        onSearch={setSearch}
        onRowClick={(user) => navigate(`/users/${user._id}`)}
      />

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        confirmText="Delete"
        loading={deleting}
      />
    </DashboardLayout>
  );
}
